import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClientProvider } from '@tanstack/react-query';
import { UserRole } from '@klaro7/shared-types';

import { queryClient } from '@/lib/queryClient';
import { AppUpdateWatcher } from '@/components/AppUpdateWatcher';
import { RateLimitBanner } from '@/components/RateLimitBanner';
import { ScrollToTop } from '@/components/ScrollToTop';
import { AuthProvider } from '@/features/auth/context/AuthContext';
import { CallProvider } from '@/features/calls/context/CallContext';
import { CallOverlay } from '@/features/calls/components/CallOverlay';
import { AvailabilityWatchProvider } from '@/features/availability-watch/context/AvailabilityWatchContext';
import { CookieConsentProvider, CookieConsentRoot } from '@/features/cookie-consent';
import { Toaster } from 'sonner';
import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { PlatformLayout } from '@/components/layout/PlatformLayout';

// Routes
import { LoginPage } from './routes/login';
import { RegisterPage } from './routes/register';
import { ForgotPasswordPage } from './routes/forgot-password';
import { ResetPasswordPage } from './routes/reset-password';
import { ChangePasswordPage } from './routes/change-password';
import { VerifyPhonePage } from './routes/verifica-telefono';
import { ConfermaEmailPage } from './routes/conferma-email';
import { DashboardHomePage } from './routes/dashboard/index';
import { ProfilePage } from './routes/dashboard/profile';
import { SettingsPage } from './routes/dashboard/settings';
import { AdminUsersPage } from './routes/dashboard/admin/users';
import { AdminProfessionalsPage } from './routes/dashboard/admin/professionals';
import { AdminWaitlistPage } from './routes/dashboard/admin/waitlist';
import { AdminServicesPage } from './routes/dashboard/admin/services';
import { AdminRevenueConfigPage } from './routes/dashboard/admin/revenue-config';
import { AdminRevenuePage } from './routes/dashboard/admin/revenue';
import { AdminReportsPage } from './routes/dashboard/admin/reports';
import { AdminCallDetailPage } from './routes/dashboard/admin/call-detail';
import { AdminConversationsPage } from './routes/dashboard/admin/conversations';
import { AdminStarredMessagesPage } from './routes/dashboard/admin/starred-messages';
import { AdminActivitiesPage } from './routes/dashboard/admin/activities';
import { AdminEmailTemplatesPage } from './routes/dashboard/admin/email-templates';
import { AdminQuestionnairesPage } from './routes/dashboard/admin/questionnaires';
import { AdminSeoPage } from './routes/dashboard/admin/seo';
import { AdminAdminsPage } from './routes/dashboard/admin/admins';
import { AdminErrorLogsPage } from './routes/dashboard/admin/error-logs';
import { AdminUserTimelinePage } from './routes/dashboard/admin/user-timeline';
import { AdminSocialPage } from './routes/dashboard/admin/social';
import { AdminTrackingPage } from './routes/dashboard/admin/tracking';
import { AdminSmsRelayPage } from './routes/dashboard/admin/sms-relay';
import { AdminMarketingConsentsPage } from './routes/dashboard/admin/marketing-consents';
import { QuestionnairePage } from './routes/questionnaire/index';
import { HomePage } from './routes/home/index';
import { ConsultantPage } from './routes/home/consultant';
import { ConsultantsPage } from './routes/consultants/index';
import { GuidePage } from './routes/legal/GuidePage';
import { CondizioniGeneraliPage } from './routes/legal/CondizioniGeneraliPage';
import { PrivacyPage } from './routes/legal/PrivacyPage';
import { CookiePolicyPage } from './routes/legal/CookiePolicyPage';
import { NoteLegaliPage } from './routes/legal/NoteLegaliPage';
import { RisoluzioneControversiePage } from './routes/legal/RisoluzioneControversiePage';
import { PolicyRecensioniPage } from './routes/legal/PolicyRecensioniPage';
import { FaqPage } from './routes/legal/FaqPage';
import { ChiSiamoPage } from './routes/legal/ChiSiamoPage';
import { CartaValoriPage } from './routes/legal/CartaValoriPage';
import { CodiceEticoPage } from './routes/legal/CodiceEticoPage';
import { ComeSelezioniamoPage } from './routes/legal/ComeSelezioniamoPage';
import { LavoraConNoiPage } from './routes/legal/LavoraConNoiPage';
import { WalletPage } from './routes/dashboard/wallet';
import { EarningsPage } from './routes/dashboard/earnings';
import { ConsultationsPage } from './routes/dashboard/consultations';
import { UserProfilePage } from './routes/dashboard/user-profile';
import { ChatPage } from '@/features/chat/components/ChatPage';
import { SupportProPage } from '@/features/support/components/SupportProPage';
import { SupportInboxPage } from '@/features/support/components/SupportInboxPage';
import { SupportUsersInboxPage } from '@/features/support-users/components/SupportUsersInboxPage';
import { SupportUserPage } from '@/features/support-users/components/SupportUserPage';
import { CallHistoryPage } from './routes/home/calls';
import { PromozioniPage } from './routes/home/promozioni';
import { PreferitiPage } from './routes/home/preferiti';
import { AnswerCallPage } from '@/features/calls/components/AnswerCallPage';
import { AdminProfessionalDetailPage } from './routes/dashboard/admin/professional-detail';
import { AdminUserDetailPage } from './routes/dashboard/admin/user-detail';
import { AdminPayoutsPage } from './routes/dashboard/admin/payouts';
import { AdminF24Page } from './routes/dashboard/admin/f24';
import { CompensiPage } from './routes/dashboard/compensi';
import { FiscalProfilePage } from './routes/dashboard/profilo-fiscale';
import { RecensioniPage } from './routes/dashboard/recensioni';
import { CompletaProfiloPage } from '@/features/onboarding/CompletaProfiloPage';
import { needsOnboarding } from '@/features/onboarding/needsOnboarding';
import { useAuth } from '@/features/auth/hooks/useAuth';

function MyRouteGate({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user?.role === UserRole.PROFESSIONAL) {
    const dashboardPath = location.pathname.replace(/^\/my/, '/dashboard');
    return <Navigate to={dashboardPath + location.search} replace />;
  }
  // Legal-compliance gate: a regular USER missing DOB or with stale Terms/Privacy
  // versions is forced through onboarding before reaching any other /my/* page.
  if (needsOnboarding(user) && location.pathname !== '/my/completa-profilo') {
    return <Navigate to="/my/completa-profilo" replace />;
  }
  return <>{children}</>;
}

/**
 * Public marketplace pages (home, consultant detail) are off-limits to
 * professionals — they should always land on /dashboard. We short-circuit
 * BEFORE the page renders to avoid a flash of the marketplace UI.
 */
function PublicRouteGate({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  if (user?.role === UserRole.PROFESSIONAL) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

export function App() {
  return (
    <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <RateLimitBanner />
      <AuthProvider>
        <CallProvider>
        <AvailabilityWatchProvider>
        <CookieConsentProvider>
        <Toaster position="bottom-right" richColors closeButton />
        <BrowserRouter>
          <ScrollToTop />
          <AppUpdateWatcher />
          <CallOverlay />
          <CookieConsentRoot />
          <Routes>
            {/* Platform layout - public pages */}
            <Route element={<PlatformLayout />}>
              <Route path="/" element={<PublicRouteGate><HomePage /></PublicRouteGate>} />
              <Route path="/consulenti" element={<PublicRouteGate><ConsultantsPage /></PublicRouteGate>} />
              <Route path="/consultant/:id" element={<PublicRouteGate><ConsultantPage /></PublicRouteGate>} />

              {/* Guida ai primi passi + pagine legali/contenuto (sul sito, landing dismessa) */}
              <Route path="/guide" element={<GuidePage />} />
              <Route path="/condizioni-generali" element={<CondizioniGeneraliPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/cookie-policy" element={<CookiePolicyPage />} />
              <Route path="/note-legali" element={<NoteLegaliPage />} />
              <Route path="/risoluzione-controversie" element={<RisoluzioneControversiePage />} />
              <Route path="/policy-recensioni" element={<PolicyRecensioniPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/chi-siamo" element={<ChiSiamoPage />} />
              <Route path="/carta-valori" element={<CartaValoriPage />} />
              <Route path="/codice-etico" element={<CodiceEticoPage />} />
              <Route path="/come-selezioniamo-i-consulenti" element={<ComeSelezioniamoPage />} />
              <Route path="/lavora-con-noi" element={<LavoraConNoiPage />} />

              {/* Authenticated user routes inside PlatformLayout */}
              <Route element={<ProtectedRoute modalLogin />}>
                <Route path="/my/consultations" element={<MyRouteGate><ConsultationsPage /></MyRouteGate>} />
                <Route path="/my/calls" element={<MyRouteGate><CallHistoryPage /></MyRouteGate>} />
                <Route path="/my/wallet" element={<MyRouteGate><WalletPage /></MyRouteGate>} />
                <Route path="/my/promozioni" element={<MyRouteGate><PromozioniPage /></MyRouteGate>} />
                <Route path="/my/preferiti" element={<MyRouteGate><PreferitiPage /></MyRouteGate>} />
                <Route path="/my/profile" element={<MyRouteGate><UserProfilePage /></MyRouteGate>} />
                <Route path="/my/completa-profilo" element={<MyRouteGate><CompletaProfiloPage /></MyRouteGate>} />
                <Route path="/my/chat" element={<MyRouteGate><ChatPage /></MyRouteGate>} />
                <Route path="/my/chat/:conversationId" element={<MyRouteGate><ChatPage /></MyRouteGate>} />
                <Route path="/my/supporto" element={<MyRouteGate><SupportUserPage /></MyRouteGate>} />
                {/* SMS answer-link: resolves the token, injects the pending
                    incoming call, then redirects home where the modal shows. */}
                <Route path="/call/:token" element={<AnswerCallPage />} />
              </Route>
            </Route>

            {/* Standalone routes (no layout wrapper) */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />
            <Route path="/verifica-telefono" element={<VerifyPhonePage />} />
            <Route path="/conferma-email" element={<ConfermaEmailPage />} />
            <Route path="/questionnaire/:token" element={<QuestionnairePage />} />

            {/* Admin/Professional dashboard (existing DashboardLayout) */}
            <Route element={<ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.PROFESSIONAL]} />}>
              <Route element={<DashboardLayout />}>
                <Route path="/dashboard" element={<DashboardHomePage />} />
                <Route path="/dashboard/profile" element={<ProfilePage />} />
                <Route path="/dashboard/settings" element={<SettingsPage />} />
                <Route path="/dashboard/consultations" element={<ConsultationsPage />} />
                <Route path="/dashboard/calls" element={<CallHistoryPage />} />
                <Route path="/dashboard/wallet" element={<EarningsPage />} />
                <Route path="/dashboard/chat" element={<ChatPage />} />
                <Route path="/dashboard/chat/:conversationId" element={<ChatPage />} />

                {/* Professional-only routes */}
                <Route element={<ProtectedRoute allowedRoles={[UserRole.PROFESSIONAL]} />}>
                  <Route path="/dashboard/support" element={<SupportProPage />} />
                  <Route path="/dashboard/compensi" element={<CompensiPage />} />
                  <Route path="/dashboard/profilo-fiscale" element={<FiscalProfilePage />} />
                  <Route path="/dashboard/recensioni" element={<RecensioniPage />} />
                </Route>

                {/* Admin routes */}
                <Route element={<ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN, UserRole.ADMIN]} />}>
                  <Route path="/dashboard/admin/users" element={<AdminUsersPage />} />
                  <Route path="/dashboard/admin/users/:id" element={<AdminUserDetailPage />} />
                  <Route path="/dashboard/admin/professionals" element={<AdminProfessionalsPage />} />
                  <Route path="/dashboard/admin/professionals/:id" element={<AdminProfessionalDetailPage />} />
                  <Route path="/dashboard/admin/waitlist" element={<AdminWaitlistPage />} />
                  <Route path="/dashboard/admin/questionnaires" element={<AdminQuestionnairesPage />} />
                  <Route path="/dashboard/admin/payouts" element={<AdminPayoutsPage />} />
                  <Route path="/dashboard/admin/f24" element={<AdminF24Page />} />
                  <Route path="/dashboard/admin/activities" element={<AdminActivitiesPage />} />
                </Route>

                {/* Super admin only routes */}
                <Route element={<ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]} />}>
                  <Route path="/dashboard/admin/services" element={<AdminServicesPage />} />
                  <Route path="/dashboard/admin/revenue" element={<AdminRevenuePage />} />
                  <Route path="/dashboard/admin/revenue/report" element={<AdminReportsPage />} />
                  <Route path="/dashboard/admin/revenue/configurazione" element={<AdminRevenueConfigPage />} />
                  <Route path="/dashboard/admin/calls/:id" element={<AdminCallDetailPage />} />
                  <Route path="/dashboard/admin/email-templates" element={<AdminEmailTemplatesPage />} />
                  <Route path="/dashboard/admin/admins" element={<AdminAdminsPage />} />
                  <Route path="/dashboard/admin/error-logs" element={<AdminErrorLogsPage />} />
                  <Route path="/dashboard/admin/users/:id/timeline" element={<AdminUserTimelinePage />} />
                  <Route path="/dashboard/admin/seo" element={<AdminSeoPage />} />
                  <Route path="/dashboard/admin/social" element={<AdminSocialPage />} />
                  <Route path="/dashboard/admin/tracking" element={<AdminTrackingPage />} />
                  <Route path="/dashboard/admin/sms-relay" element={<AdminSmsRelayPage />} />
                  <Route path="/dashboard/admin/marketing-consents" element={<AdminMarketingConsentsPage />} />
                  <Route path="/dashboard/admin/support" element={<SupportInboxPage />} />
                  <Route path="/dashboard/admin/support-users" element={<SupportUsersInboxPage />} />
                  <Route path="/dashboard/admin/conversazioni" element={<AdminConversationsPage />} />
                  <Route path="/dashboard/admin/messaggi-preferiti" element={<AdminStarredMessagesPage />} />
                </Route>
              </Route>
            </Route>

            {/* Backwards compat redirects */}
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/dashboard/user-profile" element={<Navigate to="/my/profile" replace />} />

            {/* 404 - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </CookieConsentProvider>
        </AvailabilityWatchProvider>
        </CallProvider>
      </AuthProvider>
    </QueryClientProvider>
    </HelmetProvider>
  );
}
